import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import { postAppMessage } from '@utils/appMessage';
import { CMD } from '@src/constants/appMessageCmd';
import AccessTokenManager from '@utils/storeManager/AccessTokenManager';
import queryClient from '@src/libs/react-query';
import RefreshTokenManager from '@utils/storeManager/RefreshTokenManager';
import UserInfoManager from '@utils/storeManager/UserInfoManager';
import * as Sentry from '@sentry/browser';
import AppEventManager from '@utils/storeManager/AppEventManager';

const INVALID_ACCESS_TOKEN = 'S4_0001';
const INVALID_REFRESH_TOKEN = 'S4_0002';
const EXPIRED_ACCESS_TOKEN = 'S4_0003';
const EXPIRED_REFRESH_TOKEN = 'S4_0004';
const SUSPENDED_USER = 'S4_0007';
const WITHDRAWAL_USER = 'S4_0008';
const KICKED_USER = 'S4_0009';

const handleForceLogout = () => {
  // SSR(getServerSideProps)에서는 정리할 브라우저 상태도, 이동시킬 화면도 없다.
  // queryClient 는 모듈 싱글턴이라 서버에서 clear() 하면 다른 요청의 prefetch 까지 날아간다.
  if (typeof window === 'undefined') {
    return;
  }

  queryClient.clear();
  AccessTokenManager.setAccessToken('');
  RefreshTokenManager.setRefreshToken('');
  UserInfoManager.setUserInfo(null);

  // 앱은 토큰 사본을 따로 들고 다음 페이지 로드마다 initialize 로 재주입한다.
  // 여기서 알려주지 않으면 웹이 지운 토큰이 되살아나 401/403 → 로그인 이동이 무한 반복된다.
  postAppMessage({
    cmd: CMD.STORE_AUTH_TOKEN,
    accessToken: '',
    refreshToken: '',
  });

  const currentPath = window.location.pathname;
  const currentQuery = window.location.search;
  const redirectUri = btoa(encodeURIComponent(`${currentPath}${currentQuery}`));

  AppEventManager.setAppEvent(`/login?redirectUri=${redirectUri}`);
};

const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  paramsSerializer: (params) => qs.stringify(params, { indices: false }),
});

let isTokenRefreshing = false;
let requestQueue: {
  resolve: (value: Promise<AInstance.Res<any>> | any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  reject: (reason?: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
}[] = [];

axiosInstance.interceptors.request.use(
  // @ts-expect-error axios 설정
  (config: AxiosRequestConfig) => {
    // SSR 실행 시에는 토큰 포함하지 않음
    if (typeof window !== 'undefined') {
      const token = AccessTokenManager.getAccessToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${JSON.parse(token)}`,
        };
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config;
    if (error.response) {
      /// @ts-expect-error axios 설정
      const { code } = error.response.data;

      if (code === EXPIRED_ACCESS_TOKEN) {
        if (!isTokenRefreshing) {
          isTokenRefreshing = true;

          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const response = await axiosInstance.post(
                '/api/users/token/reissue',
                {
                  refreshToken: JSON.parse(refreshToken),
                },
                {
                  headers: { Authorization: undefined },
                },
              );

              const { accessToken, refreshToken: newRefreshToken } =
                response.data;
              AccessTokenManager.setAccessToken(accessToken);
              RefreshTokenManager.setRefreshToken(newRefreshToken);
              postAppMessage({
                cmd: CMD.STORE_AUTH_TOKEN,
                accessToken,
                refreshToken: newRefreshToken,
              });

              isTokenRefreshing = false;

              // @ts-expect-error axios 설정
              originalConfig.headers.Authorization = `Bearer ${accessToken}`;

              requestQueue.forEach((p) => {
                p.resolve(`Bearer ${accessToken}`);
              });

              requestQueue = [];

              // @ts-expect-error axios 설정
              return await Promise.resolve(axiosInstance(originalConfig));
            } catch (reissueError) {
              isTokenRefreshing = false;
              requestQueue.forEach((p) => p.reject(reissueError));
              requestQueue = [];
              // handleForceLogout();
              return Promise.reject(reissueError);
            }
          }

          // 리프레시 토큰이 없으면 재발급이 불가능하다. 플래그를 되돌리지 않으면
          // 이후 모든 요청이 영영 resolve 되지 않는 큐에 쌓이므로 여기서 정리하고 로그인으로 보낸다.
          isTokenRefreshing = false;
          requestQueue.forEach((p) => p.reject(error));
          requestQueue = [];
          handleForceLogout();

          return Promise.reject(error);
        }
        return new Promise((resolve, reject) => {
          requestQueue.push({
            resolve: (token: string) => {
              // @ts-expect-error axios 설정
              originalConfig.headers.Authorization = token;

              // @ts-expect-error axios 설정
              resolve(axiosInstance(originalConfig));
            },
            reject: (err: AxiosError) => reject(err),
          });
        });
      }
      if (
        [
          INVALID_ACCESS_TOKEN,
          INVALID_REFRESH_TOKEN,
          EXPIRED_REFRESH_TOKEN,
          SUSPENDED_USER,
          WITHDRAWAL_USER,
          KICKED_USER,
        ].includes(code) ||
        error.response.status === 403
      ) {
        handleForceLogout();
      } else if (error.response.status === 401) {
        // 토큰을 실어 보낸 요청의 401 = 세션이 끊긴 것 → 로그인으로 유도한다.
        // 토큰 없이 나간 요청의 401 = 그냥 비로그인 상태 → 조용히 넘겨 익명 화면이 로그인으로 튕기지 않게 한다.
        if (originalConfig?.headers?.Authorization) {
          handleForceLogout();
        } else {
          Sentry.captureException(error);
          return {
            status: error.response.status,
            statusText: error.response.statusText,
          };
        }
      } else if (error.response.status === 400) {
        Sentry.captureException(error);
        return {
          status: error.response.status,
          statusText: error.response.statusText,
        };
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
